import React, { useEffect, useState } from 'react';
import {
    Card, Button, Typography, Row, Col, Table, Statistic, Alert, Space, Spin, message, Divider,
} from 'antd';
import { CloudDownloadOutlined, DatabaseOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../store/AuthContext';
import { PERMS } from '../constants/permissions';
import { fetchBackupInfo, downloadBackup, BackupInfo } from '../api/settingsApi';
import { downloadBlob, exportTableToExcel, exportStamp } from '../utils/excelExport';
import ExcelExportButton from '../components/Report/ExcelExportButton';

const { Title, Text, Paragraph } = Typography;

/**
 * Settings — currently the home of the system backup.
 *
 * The backup is streamed from the server as a zip of one JSON file per
 * collection, so it can be restored with `mongoimport` or read by hand.
 */
const SettingsPage: React.FC = () => {
    const { t } = useTranslation();
    const { hasPermission } = useAuth();

    const [info, setInfo] = useState<BackupInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const canBackup = hasPermission(PERMS.settings.backup);

    const loadInfo = async () => {
        if (!canBackup) return;
        setLoading(true);
        try {
            setInfo(await fetchBackupInfo());
        } catch (error: any) {
            message.error(
                error?.response?.data?.message || t('backup_info_failed') || 'Failed to read backup information'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canBackup]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const { blob, fileName } = await downloadBackup();
            downloadBlob(blob, fileName);
            message.success(t('backup_downloaded') || 'Backup downloaded');
        } catch (error: any) {
            message.error(
                error?.response?.data?.message || t('backup_failed') || 'Failed to download the backup'
            );
        } finally {
            setDownloading(false);
        }
    };

    const exportCollectionsToExcel = () => {
        if (!info) return;
        exportTableToExcel({
            fileName: `database-contents-${exportStamp()}`,
            sheetName: t('backup') as string,
            columns: [
                { header: t('collection') || 'Collection', key: 'name', width: 30 },
                { header: t('documents') || 'Documents', key: 'documents', width: 16 },
            ],
            rows: info.collections,
            footer: [[t('total') as string, info.totalDocuments]],
            title: [`${t('database') || 'Database'}: ${info.database}`, info.generatedAt],
        });
    };

    if (!canBackup) {
        return (
            <Card style={{ margin: 24 }}>
                <Alert
                    type="warning"
                    showIcon
                    message={t('access_denied_title')}
                    description={t('access_denied_message')}
                />
            </Card>
        );
    }

    return (
        <div style={{ padding: 16 }}>
            <Title level={3}>{t('settings') || 'Settings'}</Title>

            <Card
                title={
                    <Space>
                        <DatabaseOutlined />
                        {t('system_backup') || 'System backup'}
                    </Space>
                }
                style={{ marginBottom: 16 }}
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={loadInfo} loading={loading}>
                            {t('refresh') || 'Refresh'}
                        </Button>
                        <Button
                            type="primary"
                            icon={<CloudDownloadOutlined />}
                            loading={downloading}
                            onClick={handleDownload}
                        >
                            {t('download_backup') || 'Download backup'}
                        </Button>
                    </Space>
                }
            >
                <Paragraph type="secondary">
                    {t('backup_description') ||
                        'Downloads the whole database as a zip archive containing one JSON file per collection, plus a manifest. Keep it somewhere safe — it contains every invoice, customer and price in the system.'}
                </Paragraph>

                <Spin spinning={loading}>
                    {info && (
                        <>
                            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                <Col xs={24} md={8}>
                                    <Statistic title={t('database') || 'Database'} value={info.database} />
                                </Col>
                                <Col xs={12} md={8}>
                                    <Statistic title={t('collections') || 'Collections'} value={info.collections.length} />
                                </Col>
                                <Col xs={12} md={8}>
                                    <Statistic title={t('documents') || 'Documents'} value={info.totalDocuments} />
                                </Col>
                            </Row>

                            <Divider />

                            <Row justify="end" style={{ marginBottom: 12 }}>
                                <ExcelExportButton
                                    fileName="database-contents"
                                    sheetName={t('backup') as string}
                                    columns={[
                                        { header: t('collection') || 'Collection', key: 'name', width: 30 },
                                        { header: t('documents') || 'Documents', key: 'documents', width: 16 },
                                    ]}
                                    rows={info.collections}
                                    footer={[[t('total') as string, info.totalDocuments]]}
                                    title={[`${t('database') || 'Database'}: ${info.database}`, info.generatedAt]}
                                />
                            </Row>

                            <Table
                                rowKey="name"
                                size="small"
                                pagination={false}
                                dataSource={info.collections}
                                columns={[
                                    { title: t('collection') || 'Collection', dataIndex: 'name' },
                                    {
                                        title: t('documents') || 'Documents',
                                        dataIndex: 'documents',
                                        align: 'right' as const,
                                        render: (value: number) => value.toLocaleString(),
                                        sorter: (a: any, b: any) => a.documents - b.documents,
                                    },
                                ]}
                                summary={() => (
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0}>
                                            <Text strong>{t('total')}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} align="right">
                                            <Text strong>{info.totalDocuments.toLocaleString()}</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                )}
                            />
                        </>
                    )}
                </Spin>
            </Card>

            <Card title={t('restore') || 'Restore'}>
                <Alert
                    type="info"
                    showIcon
                    message={t('restore_hint_title') || 'Restoring a backup'}
                    description={
                        t('restore_hint') ||
                        'Unzip the archive and load each JSON file with `mongoimport --jsonArray --collection <name> --file <name>.json`. Restore is deliberately not exposed in the UI — overwriting live data should be a deliberate, supervised action.'
                    }
                />
            </Card>
        </div>
    );
};

export default SettingsPage;
